import { Room } from '../types/user';

/**
 * Room invite links + share messages — a single source of truth for every share
 * surface (dashboard cards, in-room Share button, deep links).
 *
 * WHY A SHIM: WhatsApp/Facebook/Twitter/Telegram crawlers do NOT run our SPA's
 * JavaScript, so react-helmet's per-room OG tags never reach them and the shared
 * link shows the generic default card. To make the ROOM THUMBNAIL appear in the
 * WhatsApp preview, the link must resolve to server-rendered OG tags.
 *
 * Two delivery modes (see the AskUserQuestion decision — "Both"):
 *   - 'backend'  → https://sync.direct/api/i/{code}?...  (ASP.NET InviteOgController
 *                  renders OG + thumbnail, then redirects humans to the room).
 *                  Ships today, no AWS change.
 *   - 'branded'  → https://gamedate.co.il/room/{code}?...  (a CloudFront edge
 *                  function serves crawlers the same OG HTML; humans pass through
 *                  to the SPA). Flip to this once the CloudFront function is live.
 *
 * The link always carries the exact scene template id (w) so the correct
 * thumbnail is chosen even for ephemeral rooms, plus who-invited (from) and the
 * note — both surfaced on the room-join card and, in branded mode, read by the
 * crawler function too.
 */

export type InviteLinkMode = 'backend' | 'branded';

/** Active mode. 'branded' = links stay on gamedate.co.il/room/{code}; the CloudFront
 *  `gamedate-room-og` viewer-request function (scoped to the /room/* behavior on
 *  distribution E24EVZULRBPD) serves crawlers the OG card. Deployed 2026-07-15. */
export const INVITE_LINK_MODE: InviteLinkMode = 'branded';

/** Web origin that hosts the SPA, room pages, and world-thumbs. */
export const WEB_ORIGIN = 'https://gamedate.co.il';
/** Backend origin that serves the server-rendered OG link shim. */
export const OG_SHIM_ORIGIN = 'https://sync.direct';

export interface InviteContext {
  room: Pick<Room, 'code' | 'name' | 'sceneTemplateId' | 'hostName'>;
  /** Display name of the person sending the invite (who-invited). */
  inviterName?: string | null;
  /** Custom invitation note; falls back to a sensible default when empty. */
  note?: string | null;
}

/** Absolute URL of a room's world thumbnail (640×384), or the default OG image. */
export function roomThumbUrl(room: Pick<Room, 'sceneTemplateId'>): string {
  return room.sceneTemplateId
    ? `${WEB_ORIGIN}/world-thumbs/${room.sceneTemplateId}.jpg`
    : `${WEB_ORIGIN}/og-image.png`;
}

/** The canonical (branded) room page a human lands on. */
export function roomPageUrl(code: string): string {
  return `${WEB_ORIGIN}/room/${encodeURIComponent(code)}`;
}

/** Suggested default invitation note when the user hasn't typed one. */
export function defaultInviteNote({ room, inviterName }: InviteContext): string {
  const who = inviterName?.trim() || 'Someone';
  return `${who} invited you to join "${room.name}" on GameDate! 🎮`;
}

function appendParam(sb: string[], key: string, value?: string | null) {
  if (value != null && value !== '') {
    sb.push(`${key}=${encodeURIComponent(value)}`);
  }
}

/**
 * The shareable invite link. In 'backend' mode this is the sync.direct OG shim
 * (rich WhatsApp preview → redirect); in 'branded' mode it's the gamedate.co.il
 * room page directly (CloudFront serves crawlers the preview).
 */
export function buildInviteLink({ room, inviterName, note }: InviteContext): string {
  const code = encodeURIComponent(room.code);
  const params: string[] = [];
  appendParam(params, 'w', room.sceneTemplateId);
  appendParam(params, 'from', inviterName?.trim());
  appendParam(params, 'note', note?.trim());
  // Room name (n): the branded CloudFront edge fn can't do a DB lookup, so it needs
  // the name in the query to title the card; the backend shim uses it as a fallback.
  appendParam(params, 'n', room.name);

  if (INVITE_LINK_MODE === 'branded') {
    const qs = params.length ? `?${params.join('&')}` : '';
    return `${WEB_ORIGIN}/room/${code}${qs}`;
  }

  // backend: path is /api/i/* so it rides the backend's existing Caddy reverse-proxy route.
  const qs = params.length ? `?${params.join('&')}` : '';
  return `${OG_SHIM_ORIGIN}/api/i/${code}${qs}`;
}

/** The message body (note + link) for WhatsApp / native share / email body. */
export function buildInviteMessage(ctx: InviteContext): string {
  const note = ctx.note?.trim() || defaultInviteNote(ctx);
  const link = buildInviteLink(ctx);
  return `${note}\n\n${link}`;
}

/** WhatsApp "share to any chat" deep link (opens app on mobile, web on desktop). */
export function buildWhatsAppUrl(ctx: InviteContext): string {
  return `https://wa.me/?text=${encodeURIComponent(buildInviteMessage(ctx))}`;
}

/** mailto: link with a friendly subject + note/link body. */
export function buildMailtoUrl(ctx: InviteContext): string {
  const subject = `Join "${ctx.room.name}" on GameDate`;
  const body = buildInviteMessage(ctx);
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/** Web Share API payload (native OS share sheet — SMS, Telegram, IG DMs, …). */
export function buildWebSharePayload(ctx: InviteContext): ShareData {
  return {
    title: `Join "${ctx.room.name}" on GameDate`,
    text: ctx.note?.trim() || defaultInviteNote(ctx),
    url: buildInviteLink(ctx),
  };
}
